import React, { useState } from 'react'
import dayjs from 'dayjs'
import styles from './CommandHistory.module.scss'
import { useCommandHistory } from '../../hooks/useCommandHistory'
import {
  CommandHistory as CommandHistoryRow,
  CommandStatus,
  ResponseStatus,
} from '../../types/commandBus.types'

interface CommandHistoryProps {
  devEui: string
  pageSize?: number
}

const STATUS_LABEL: Record<CommandStatus, { label: string; className: string }> = {
  queued: { label: 'En file', className: 'statusQueued' },
  sent_to_chirpstack: { label: 'Envoyée', className: 'statusSent' },
  confirmed: { label: 'Confirmée', className: 'statusConfirmed' },
  failed: { label: 'Échec', className: 'statusFailed' },
  timeout: { label: 'Timeout', className: 'statusTimeout' },
}

const StatusPill: React.FC<{ status: CommandStatus }> = ({ status }) => {
  const cfg = STATUS_LABEL[status] ?? { label: status, className: 'statusQueued' }
  return (
    <span className={[styles.statusBadge, styles[cfg.className]].join(' ')}>
      {cfg.label}
    </span>
  )
}

const RESPONSE_LABEL: Record<ResponseStatus, { label: string; className: string }> = {
  received: { label: 'Reçue', className: 'statusConfirmed' },
  timeout: { label: 'Timeout', className: 'statusTimeout' },
}

const ResponseBadge: React.FC<{ status?: ResponseStatus }> = ({ status }) => {
  if (!status) {
    return (
      <span className={[styles.statusBadge, styles.statusQueued].join(' ')}>
        En attente
      </span>
    )
  }
  const cfg = RESPONSE_LABEL[status] ?? { label: status, className: 'statusQueued' }
  return (
    <span className={[styles.statusBadge, styles[cfg.className]].join(' ')}>
      {cfg.label}
    </span>
  )
}

// One label/value line in the expanded detail panel.
const DetailLine: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className={styles.detailLine}>
    <span className={styles.detailLabel}>{label}</span>
    <span className={mono ? styles.detailMono : styles.detailValue}>{value}</span>
  </div>
)

// A label + pretty-printed JSON block (params / device response).
const DetailBlock: React.FC<{ label: string; obj?: Record<string, unknown> }> = ({
  label,
  obj,
}) => {
  const empty = !obj || Object.keys(obj).length === 0
  return (
    <div className={styles.detailBlock}>
      <span className={styles.detailLabel}>{label}</span>
      {empty ? (
        <span className={styles.detailValue}>—</span>
      ) : (
        <pre className={styles.detailJson}>{JSON.stringify(obj, null, 2)}</pre>
      )}
    </div>
  )
}

const HistoryRow: React.FC<{ cmd: CommandHistoryRow }> = ({ cmd }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={[styles.row, open ? styles.rowOpen : ''].join(' ')}>
      <button
        type="button"
        className={styles.rowHeader}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.rowDate}>
          {dayjs(cmd.created_at).format('DD/MM HH:mm:ss')}
        </span>
        <span className={styles.rowCmd}>{cmd.command_name}</span>
        <span className={styles.rowBadges}>
          <StatusPill status={cmd.status} />
          <ResponseBadge status={cmd.response_status} />
        </span>
        <span className={styles.rowArrow} data-open={open ? 'true' : 'false'}>
          ›
        </span>
      </button>

      {open && (
        <div className={styles.detail}>
          <DetailLine label="Source" value={cmd.source ?? 'system'} mono />
          <DetailLine label="Correlation" value={cmd.correlation_id ?? '—'} mono />
          {cmd.chirpstack_queue_id && (
            <DetailLine label="ChirpStack" value={cmd.chirpstack_queue_id} mono />
          )}
          <DetailBlock label="Paramètres" obj={cmd.params} />
          <DetailBlock label="Réponse du device" obj={cmd.device_response} />
          <DetailLine
            label="Créée"
            value={dayjs(cmd.created_at).format('DD/MM/YYYY HH:mm:ss')}
          />
          {cmd.updated_at && (
            <DetailLine
              label="Mise à jour"
              value={dayjs(cmd.updated_at).format('DD/MM/YYYY HH:mm:ss')}
            />
          )}
          {cmd.error && <DetailLine label="Erreur" value={cmd.error} />}
        </div>
      )}
    </div>
  )
}

export default function CommandHistory({ devEui, pageSize = 20 }: CommandHistoryProps) {
  const [page, setPage] = useState(1)
  const query = useCommandHistory(devEui, page, pageSize)

  const rows = query.data?.data ?? []
  const pagination = query.data?.pagination
  const totalPages = pagination?.total_pages ?? 0
  const currentPage = pagination?.current_page ?? page

  if (query.isError) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Historique des commandes</h3>
        </div>
        <div className={styles.error}>
          Impossible de récupérer l'historique des commandes
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Historique des commandes</h3>
        {pagination && pagination.total_count > 0 && (
          <span className={styles.pageInfo}>
            {pagination.total_count} commande{pagination.total_count > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {query.isLoading ? (
        <div className={styles.loading}>Chargement…</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>Aucune commande envoyée à ce device.</div>
      ) : (
        <div className={styles.list}>
          {rows.map((cmd) => (
            <HistoryRow key={cmd.command_id} cmd={cmd} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageButton}
            disabled={currentPage <= 1 || query.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Précédent
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageButton}
            disabled={currentPage >= totalPages || query.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  )
}
